import { useEffect } from 'react';
import { usePageSEO } from '@/hooks/usePageSEO';
import FadeInSection from '@/components/base/FadeInSection';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import Hero from './components/Hero';
import ConnaitreQuartier from './components/ConnaitreQuartier';
import ProfilToitures from './components/ProfilToitures';
import ServicesQuartier from './components/ServicesQuartier';
import PrixTypiques from './components/PrixTypiques';
import ProjetsRecents from './components/ProjetsRecents';
import TemoignageLocal from './components/TemoignageLocal';
import ZonesProches from './components/ZonesProches';
import CTAFinal from './components/CTAFinal';

function setMeta(name: string, content: string) {
  let meta = document.querySelector(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', name);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

function removeOldLdJson() {
  const existing = document.querySelectorAll('script[type="application/ld+json"]');
  existing.forEach((el) => {
    if (el.id === 'pintendre-ld') el.remove();
  });
}

const breadcrumbItems = [
  { label: 'Accueil', href: '/' },
  { label: 'Secteurs', href: null },
  { label: 'Pintendre', href: '/secteurs/pintendre' },
];

export default function PintendrePage() {
  usePageSEO({
    title: "Couvreur à Pintendre | Lévis | Fermes, Granges & Résidentiel | RBQ",
    description: "Couvreur à Pintendre, Lévis. Toiture fermes, granges agricoles et résidences rurales. Soumission gratuite en 2h. Couvreurs RBQ certifiés. 418-476-9950.",
    urlPath: "/secteurs/pintendre",
    keywords: "couvreur Pintendre, toiture Pintendre, réfection Pintendre, bardeau Lévis, tôle garage, grange, bâtiment agricole, maison bois rond, soumission",
    ogTitle: "Couvreur à Pintendre — Toiture fermes et résidences rurales",
    ogDescription: "Spécialiste des toitures agricoles et résidentielles à Pintendre. Réfection de granges, bâtiments agricoles et maisons en bois rond. Soumission gratuite.",
    ogImage: 'https://couvreur-levis.com/og/og-pintendre.jpg',
    ogImageAlt: 'Couvreur à Pintendre, Lévis - Toiture fermes et résidences rurales',
    geoPosition: "46.7123;-71.2556",
    geoPlacename: "Pintendre, Lévis, QC",
    geoRegion: "CA-QC",
  });

  useEffect(() => {
    document.title = "Couvreur à Pintendre | Lévis | Fermes, Granges & Résidentiel | RBQ";
    setMeta('description', "Couvreur à Pintendre, Lévis. Toiture fermes, granges agricoles et résidences rurales. Soumission gratuite en 2h. Couvreurs RBQ certifiés. 418-476-9950.");
    setMeta('keywords', 'couvreur Pintendre, toiture Pintendre, réfection Pintendre, bardeau Lévis, tôle garage, grange, bâtiment agricole, maison bois rond, soumission');
    setMeta('geo.position', '46.7123;-71.2556');
    setMeta('geo.placename', 'Pintendre, Lévis, QC');
    setMeta('geo.region', 'CA-QC');

    removeOldLdJson();

    const ld = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'LocalBusiness',
          name: 'Couvreur-Lévis.com — Pintendre',
          url: 'https://couvreur-levis.com/secteurs/pintendre',
          telephone: '+1-418-476-9950',
          email: 'contact@couvreur-levis.com',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Pintendre',
            addressRegion: 'QC',
            addressCountry: 'CA',
          },
          geo: {
            '@type': 'GeoCoordinates',
            latitude: 46.7196,
            longitude: -71.2782,
          },
          openingHoursSpecification: [
            {
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
              opens: '07:00',
              closes: '18:00',
            },
          ],
          priceRange: '$$',
          areaServed: {
            '@type': 'Place',
            name: 'Pintendre, Lévis',
          },
          description:
            "Couvreur à Pintendre, Lévis. Toiture fermes, granges agricoles et résidences rurales. Soumission gratuite en 2h. Couvreurs RBQ certifiés. 418-476-9950.",
          image: 'https://couvreur-levis.com/og/og-pintendre.jpg',
          hasMap: 'https://www.google.com/maps?q=Pintendre,L%C3%A9vis,QC',
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: breadcrumbItems.map((item, idx) => ({
            '@type': 'ListItem',
            position: idx + 1,
            name: item.label,
            item: item.href ? `https://couvreur-levis.com${item.href}` : undefined,
          })),
        },
        {
          '@type': 'Organization',
          name: 'Couvreur-Lévis.com',
          url: 'https://couvreur-levis.com',
          logo: 'https://couvreur-levis.com/og-default.jpg',
          description: 'Plateforme de mise en relation avec des couvreurs certifiés à Lévis et environs.',
          telephone: '+1-418-476-9950',
          email: 'contact@couvreur-levis.com',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Lévis',
            addressRegion: 'QC',
            addressCountry: 'CA',
          },
        },
        {
          '@type': 'Service',
          serviceType: 'Services de toiture',
          provider: { '@type': 'LocalBusiness', '@id': 'https://couvreur-levis.com/#business' },
          areaServed: {
            '@type': 'Place',
            name: 'Pintendre',
            containedInPlace: { '@type': 'City', name: 'Lévis' },
          },
          description: "Couvreurs partenaires à Pintendre, Lévis. Toiture, réfection, réparation, urgence. Soumission gratuite en 2h.",
        },
      ],
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'pintendre-ld';
    script.textContent = JSON.stringify(ld);
    document.head.appendChild(script);

    return () => {
      removeOldLdJson();
    };
  }, []);

  return (
    <div className="font-inter min-h-screen bg-white">
      <Header />
      <main>
        <Hero />
        <FadeInSection delay={100}><ConnaitreQuartier /></FadeInSection>
        <FadeInSection delay={200}><ProfilToitures /></FadeInSection>
        <FadeInSection delay={300}><ServicesQuartier /></FadeInSection>
        <FadeInSection delay={400}><PrixTypiques /></FadeInSection>
        <FadeInSection delay={500}><ProjetsRecents /></FadeInSection>
        <FadeInSection delay={600}><TemoignageLocal /></FadeInSection>
        <FadeInSection delay={700}><ZonesProches /></FadeInSection>
        <FadeInSection delay={800}><CTAFinal /></FadeInSection>
      </main>
      <Footer />
    </div>
  );
}